import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { UserPlus, Pencil, Trash2, KeyRound, Loader2, Copy, Check, ShieldCheck, ShieldOff, Users, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { formatDisplayDateTime } from '@/utils/dateFormat';

function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { score: Math.round(score / 6 * 100), label: 'Weak', color: 'bg-red-500' };
  if (score <= 3) return { score: Math.round(score / 6 * 100), label: 'Fair', color: 'bg-amber-500' };
  if (score <= 4) return { score: Math.round(score / 6 * 100), label: 'Good', color: 'bg-blue-500' };
  return { score: Math.round(score / 6 * 100), label: 'Strong', color: 'bg-green-500' };
}

function isPasswordValid(pw) {
  return pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /[0-9]/.test(pw);
}

const ROLE_CLS = {
  superadmin: 'bg-primary/10 text-primary',
  admin: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
  manager: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  viewer: 'bg-muted text-muted-foreground',
};
const ROLE_HINT = {
  superadmin: 'everything, incl. keys and users',
  admin: 'all data uploads + reports',
  manager: 'analytics + AI',
  viewer: 'read-only',
};
const FALLBACK_ORDER = ['superadmin', 'admin', 'manager', 'viewer'];

// A cell of the access table: a tick, a dash, or a word like "core only".
function Cell({ value }) {
  if (value === 'yes') return <Check size={14} className="text-primary mx-auto" strokeWidth={2.5} />;
  if (value === 'no') return <span className="text-muted-foreground">&ndash;</span>;
  return <span className="text-[10px] text-muted-foreground">{value}</span>;
}

export default function UserManagementPage() {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'viewer' });
  const [creating, setCreating] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '' });
  const [saving, setSaving] = useState(false);
  // Password reset modal
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState('');
  const [pwVisible, setPwVisible] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [copiedPw, setCopiedPw] = useState(false);
  // Activate/Deactivate confirmation
  const [toggleUser, setToggleUser] = useState(null);

  // The access table is rendered from the backend, next to the guards it describes. The three
  // cards that used to sit here were typed by hand and had gone stale - they named pages roles
  // no longer had and left out ones they did.
  const [roles, setRoles] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data);
    } catch (e) { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, [api]);
  useEffect(() => { api.get('/auth/roles').then(r => setRoles(r.data)).catch(() => setRoles(null)); }, [api]);

  const handleCreate = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) { toast.error('All fields required'); return; }
    if (!isPasswordValid(newUser.password)) { toast.error('Password must be 8+ chars with uppercase, lowercase, and digit'); return; }
    setCreating(true);
    try {
      await api.post('/auth/register', newUser);
      toast.success(`User created with ${newUser.role} role`);
      setNewUser({ name: '', email: '', password: '', role: 'viewer' });
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setCreating(false); }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await api.patch(`/auth/users/${editUser.id}`, editForm);
      toast.success('User updated');
      setEditUser(null);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async () => {
    if (!toggleUser) return;
    try {
      await api.patch(`/auth/users/${toggleUser.id}`, { active: toggleUser.active === false ? true : false });
      toast.success(toggleUser.active === false ? 'User activated' : 'User deactivated');
      setToggleUser(null);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); setToggleUser(null); }
  };

  const handleDelete = async (u) => {
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success('User deleted');
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleAutoGenerate = async () => {
    if (!pwUser) return;
    try {
      const res = await api.post(`/auth/users/${pwUser.id}/generate-password`);
      setPwValue(res.data.password);
      setPwVisible(true);
    } catch (err) { toast.error('Failed to generate'); }
  };

  const handleSetPassword = async () => {
    if (!pwUser || !pwValue) return;
    if (!isPasswordValid(pwValue)) { toast.error('Password too weak. Needs 8+ chars, uppercase, lowercase, digit.'); return; }
    setPwSaving(true);
    try {
      await api.post(`/auth/users/${pwUser.id}/set-password`, { password: pwValue });
      toast.success(`Password set for ${pwUser.name}`);
      setPwUser(null);
      setPwValue('');
      setPwVisible(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to set password'); }
    finally { setPwSaving(false); }
  };

  const copyPassword = async () => {
    await navigator.clipboard.writeText(pwValue);
    setCopiedPw(true);
    setTimeout(() => setCopiedPw(false), 2000);
  };

  const isSelf = (u) => u.id === currentUser?.id;

  const roleOrder = roles?._order || FALLBACK_ORDER;
  const roleLabel = (r) => roles?.[r]?.label || r;

  const formatDate = (iso) => formatDisplayDateTime(iso);

  const roleBadge = (role) => (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_CLS[role] || ROLE_CLS.viewer}`}>
      {roleLabel(role)}
    </span>
  );

  const newPwStrength = getPasswordStrength(newUser.password);
  const resetPwStrength = getPasswordStrength(pwValue);

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">User Management</h1>
        <p className="text-sm text-muted-foreground mt-1">Create, edit, and manage user accounts</p>
      </div>

      {/* Create User */}
      <div className="bg-card border border-border rounded-md p-5">
        <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2">
          <UserPlus size={16} strokeWidth={1.5} /> Create New User
        </h3>
        <form autoComplete="off" onSubmit={e => e.preventDefault()} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="space-y-1">
            <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Name</Label>
            <Input placeholder="Full name" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} data-testid="new-user-name" autoComplete="off" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Email</Label>
            <Input type="email" placeholder="email@company.com" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} data-testid="new-user-email" autoComplete="off" name="new-user-email-field" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Password</Label>
            <Input type="password" placeholder="Min 8 characters" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} data-testid="new-user-password" autoComplete="new-password" name="new-user-pw-field" />
            {newUser.password && (
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden"><div className={`h-full ${newPwStrength.color} transition-all`} style={{ width: `${newPwStrength.score}%` }} /></div>
                <span className={`text-[10px] font-medium ${newPwStrength.score <= 33 ? 'text-red-500' : newPwStrength.score <= 66 ? 'text-amber-500' : 'text-green-500'}`}>{newPwStrength.label}</span>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Role</Label>
            <Select value={newUser.role} onValueChange={v => setNewUser({...newUser, role: v})}>
              <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {roleOrder.map(r => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={handleCreate} disabled={creating || (newUser.password && !isPasswordValid(newUser.password))} data-testid="create-user-btn" className="w-full">
              {creating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <UserPlus size={14} className="mr-1.5" />}
              Create
            </Button>
          </div>
        </form>

        {/* What each level can do. Served by /auth/roles, beside the guards it describes. */}
        <div className="mt-5 pt-4 border-t border-border/50">
          <p className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3">Role Access Details</p>
          {!roles?._matrix ? (
            <p className="text-xs text-muted-foreground">Loading...</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full min-w-[620px] border-collapse" data-testid="role-matrix">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="w-[46%] p-2.5 text-left text-[11px] font-heading uppercase tracking-widest text-muted-foreground font-medium"></th>
                    {roleOrder.map(r => (
                      <th key={r} className="p-2.5 text-center text-[11px] font-heading font-semibold">{roleLabel(r)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {roles._matrix.map(row => (
                    <tr key={row.label} className="border-t border-border/60">
                      <td className="p-2.5 text-[11px] text-foreground">{row.label}</td>
                      {roleOrder.map(r => (
                        <td key={r} className="p-2.5 text-center text-[11px]"><Cell value={row.cells[r]} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {roles?._core_pages && (
            <p className="text-[10px] text-muted-foreground mt-2">Core analytics = {roles._core_pages}.</p>
          )}
        </div>
      </div>

      {/* User List */}
      <div className="bg-card border border-border rounded-md p-5 overflow-auto">
        <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2">
          <Users size={16} strokeWidth={1.5} /> {users.length} Users
        </h3>
        {loading ? <p className="text-sm text-muted-foreground text-center py-8">Loading...</p> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Name</TableHead>
                <TableHead className="text-xs">Email</TableHead>
                <TableHead className="text-xs">Role</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs">Last Login</TableHead>
                <TableHead className="text-xs">Last IP</TableHead>
                <TableHead className="text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u, i) => (
                <TableRow key={u.id || i} className={u.active === false ? 'opacity-50' : ''} data-testid={`user-row-${i}`}>
                  <TableCell className="text-sm font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell>{roleBadge(u.role)}</TableCell>
                  <TableCell>
                    <span className={`text-xs font-medium ${u.active === false ? 'text-red-500' : 'text-green-600 dark:text-green-400'}`}>
                      {u.active === false ? 'Inactive' : 'Active'}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(u.last_login_at)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{u.last_login_ip || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditUser(u); setEditForm({ name: u.name, email: u.email, role: u.role }); }}
                        data-testid={`edit-user-${i}`} className="p-1.5 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors" title="Edit">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => { setPwUser(u); setPwValue(''); setPwVisible(false); }}
                        data-testid={`reset-pw-${i}`} className="p-1.5 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors" title="Reset password">
                        <KeyRound size={13} />
                      </button>
                      {!isSelf(u) && (
                        <button onClick={() => setToggleUser(u)}
                          data-testid={`toggle-active-${i}`} className={`p-1.5 rounded hover:bg-muted/50 transition-colors ${u.active === false ? 'text-green-600' : 'text-amber-600'}`}
                          title={u.active === false ? 'Activate' : 'Deactivate'}>
                          {u.active === false ? <ShieldCheck size={13} /> : <ShieldOff size={13} />}
                        </button>
                      )}
                      {!isSelf(u) && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`delete-user-${i}`} className="p-1.5 rounded hover:bg-muted/50 text-muted-foreground hover:text-destructive transition-colors" title="Delete">
                              <Trash2 size={13} />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid="delete-user-dialog" onInteractOutside={e => e.preventDefault()}>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-heading">Delete User</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete <strong>{u.name}</strong> ({u.email})? The account will be soft-deleted and can be recovered by an admin.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(u)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                data-testid="confirm-delete-user">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Activate/Deactivate Confirmation Dialog */}
      <AlertDialog open={!!toggleUser} onOpenChange={v => { if (!v) setToggleUser(null); }}>
        <AlertDialogContent data-testid="toggle-active-dialog" onInteractOutside={e => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              {toggleUser?.active === false ? 'Activate' : 'Deactivate'} User
            </AlertDialogTitle>
            <AlertDialogDescription>
              {toggleUser?.active === false
                ? <>Are you sure you want to activate <strong>{toggleUser?.name}</strong>? They will be able to log in again.</>
                : <>Are you sure you want to deactivate <strong>{toggleUser?.name}</strong>? They will not be able to log in until reactivated.</>
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setToggleUser(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleToggleActive} data-testid="confirm-toggle-active"
              className={toggleUser?.active === false ? '' : 'bg-amber-600 text-white hover:bg-amber-700'}>
              {toggleUser?.active === false ? 'Activate' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={v => { if (!v) setEditUser(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog" onInteractOutside={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User</DialogTitle>
            <DialogDescription>Modify user details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-user-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} data-testid="edit-user-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Role</Label>
              <Select value={editForm.role} onValueChange={v => setEditForm({...editForm, role: v})}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roleOrder.map(r => (
                    <SelectItem key={r} value={r}>{roleLabel(r)}{ROLE_HINT[r] ? ` (${ROLE_HINT[r]})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button size="sm" onClick={handleEdit} disabled={saving} data-testid="save-edit-user">
              {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={!!pwUser} onOpenChange={v => { if (!v) { setPwUser(null); setPwValue(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="reset-pw-dialog" onInteractOutside={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">Reset Password</DialogTitle>
            <DialogDescription>{pwUser?.name} ({pwUser?.email})</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">New Password</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input type={pwVisible ? 'text' : 'password'} placeholder="Enter or generate password" value={pwValue}
                    onChange={e => setPwValue(e.target.value)} data-testid="reset-pw-input" />
                  <button type="button" onClick={() => setPwVisible(!pwVisible)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {pwVisible ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <Button variant="outline" size="icon" onClick={handleAutoGenerate} title="Auto-generate strong password" data-testid="auto-gen-pw-btn">
                  <RefreshCw size={14} />
                </Button>
                {pwValue && (
                  <Button variant="outline" size="icon" onClick={copyPassword} title="Copy">
                    {copiedPw ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                  </Button>
                )}
              </div>
              {/* Strength indicator */}
              {pwValue && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${resetPwStrength.color} transition-all duration-300`} style={{ width: `${resetPwStrength.score}%` }} />
                    </div>
                    <span className={`text-xs font-medium min-w-[40px] ${resetPwStrength.score <= 33 ? 'text-red-500' : resetPwStrength.score <= 66 ? 'text-amber-500' : 'text-green-500'}`}>
                      {resetPwStrength.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                    <span className={pwValue.length >= 8 ? 'text-green-600 dark:text-green-400' : ''}>8+ chars {pwValue.length >= 8 ? '\u2713' : ''}</span>
                    <span className={/[A-Z]/.test(pwValue) ? 'text-green-600 dark:text-green-400' : ''}>Uppercase {/[A-Z]/.test(pwValue) ? '\u2713' : ''}</span>
                    <span className={/[a-z]/.test(pwValue) ? 'text-green-600 dark:text-green-400' : ''}>Lowercase {/[a-z]/.test(pwValue) ? '\u2713' : ''}</span>
                    <span className={/[0-9]/.test(pwValue) ? 'text-green-600 dark:text-green-400' : ''}>Digit {/[0-9]/.test(pwValue) ? '\u2713' : ''}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setPwUser(null); setPwValue(''); }}>Cancel</Button>
            <Button size="sm" onClick={handleSetPassword} disabled={pwSaving || !pwValue || !isPasswordValid(pwValue)} data-testid="save-pw-btn">
              {pwSaving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <KeyRound size={14} className="mr-1.5" />}
              Save Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
